import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { FormInputText } from '../../../../inputs/FormInputText';
import { ButtonClassNames, InputRegexPattern } from '../../../../../models/Common';
import { FormInputPassword } from '../../../../inputs/FormInputPassword';
import { CustomMUIButton } from '../../../buttons/CustomMUIButton';
import { macAddressExample } from '../../../../../constants/configuration';
import { iptvApi } from '../../../../../services/apiServices/user/iptv';
import { useCustomSnackbar } from '../../../../../hooks/useCustomEnqueueSnackbar';
import { getErrorMessage } from '../../../../../utils';

interface IptvDeviceForm {
  login: string;
  mac: string;
  password: string;
  passwordConfirmation: string;
}

interface AddNewIptvDeviceProps {
  accountId: number | string;
  handleClose: () => void;
}

export const AddNewIptvDevice: FC<AddNewIptvDeviceProps> = ({ accountId, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<IptvDeviceForm>();
  const { handleSubmit, watch, register } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [addIptvDevice, { isLoading }] = iptvApi.useAddDeviceMutation();

  const passwordInput = useRef({});
  passwordInput.current = watch('password', '');
  const passwordConfirmRule = {
    validate: (value: string) => value === passwordInput.current || t('validation.passwordsDoNotMatch').toString(),
  };

  const handleAddDevice = async ({ password, login, mac }: IptvDeviceForm) => {
    try {
      await addIptvDevice({ accountId, login, password, mac })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.newIptvDeviceAdded'), 'success');
          handleClose();
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.addNewIptvDevice')}</article>
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleAddDevice)}>
          <FormInputText
            name="login"
            className="primary"
            label={t('common.login')}
            helperText={t('input.numberOfCharactersInLowerCaseNumbersSigns', { from: 3, to: 16 })}
            isRequired
            minLength={3}
            maxLength={16}
            pattern={InputRegexPattern.InternetLogin}
          />
          <FormInputText
            name="mac"
            className="primary"
            label={t('input.macAddress')}
            helperText={t('input.example', { val: macAddressExample })}
            isRequired
            minLength={17}
            maxLength={17}
            pattern={InputRegexPattern.MacAddress}
          />
          <FormInputPassword
            name="password"
            label={t('auth.newPassword')}
            className="primary"
            isRequired
            pattern={/^[a-z0-9_]*$/i}
            helperText={t('input.numberOfCharactersInLatinNumbers_', { from: 6, to: 16 })}
            ref={(e) => register('password', { value: e?.value || '' })}
            minLength={6}
            maxLength={16}
          />
          <FormInputPassword
            name="passwordConfirmation"
            label={t('auth.repeatNewPassword', passwordConfirmRule)}
            ref={() => register('passwordConfirmation', passwordConfirmRule)}
            className="primary"
            isRequired
          />
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('buttons.save')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
